import { useEffect, useState } from 'react'
import { isDemoMode } from '../lib/demo/flag'

const BOOKING_URL =
  'https://bookings.cloud.microsoft/book/Salesmeeting@iuvotech.com/s/YcDvqOr-vUy45H9WbceZcw2?ismsaljsauthenabled'

// Buttons whose visible text (case-insensitive substring) opens the
// demo CTA modal instead of executing. These are all actions that
// would normally hit a real backend (writes, downloads, refreshes,
// scans); in the demo they're stub-success'd by demoAdapter so the
// click would otherwise feel "dead."
const DEAD_BUTTON_KEYWORDS: string[] = [
  // Downloads / exports
  'download baseline',
  'download pdf',
  'export executive pdf',
  'export audit',
  'export',
  // Scans / refreshes
  'start new scan',
  'scan now',
  're-run scan',
  'rerun scan',
  'refresh baseline',
  'run full evaluation',
  // Drift queue actions
  'commit changes',
  'commit now',
  'approve',
  'reject',
  // Generic CTAs that appear without a backing endpoint in demo
  'request a demo',
  'contact sales',
]

function matchesDeadAction(el: HTMLElement): boolean {
  // Walk up to find the nearest button (or anchor with empty href).
  let node: HTMLElement | null = el
  for (let i = 0; node && i < 4; i++) {
    if (node.tagName === 'BUTTON') {
      const text = (node.innerText || node.getAttribute('aria-label') || node.title || '')
        .toLowerCase()
        .trim()
      if (!text) return false
      return DEAD_BUTTON_KEYWORDS.some((kw) => text.includes(kw))
    }
    if (node.tagName === 'A') {
      const href = node.getAttribute('href') || ''
      if (href === '' || href === '#' || href.startsWith('javascript:')) {
        const text = (node.innerText || node.title || '').toLowerCase().trim()
        // Only intercept anchors with empty href if their label also
        // matches one of the dead-action keywords — keeps tab anchors
        // (href="#") that are wired through React handlers alone.
        if (DEAD_BUTTON_KEYWORDS.some((kw) => text.includes(kw))) return true
      }
      return false
    }
    node = node.parentElement
  }
  return false
}

export function DemoCta() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!isDemoMode || typeof window === 'undefined') return

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      if (matchesDeadAction(target)) {
        e.preventDefault()
        e.stopPropagation()
        setOpen(true)
      }
    }
    // Capture phase so we beat React's synthetic listeners.
    document.addEventListener('click', onClick, true)

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)

    return () => {
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  if (!isDemoMode || !open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-cta-title"
      onClick={() => setOpen(false)}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99998,
        background: 'rgba(8, 12, 22, 0.72)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 460,
          width: '100%',
          background: '#121826',
          color: '#f1f5f9',
          borderRadius: 16,
          border: '1px solid rgba(116, 212, 154, 0.25)',
          padding: '32px 32px 28px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          fontFamily: 'Inter, system-ui, sans-serif',
          position: 'relative',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 10px',
            background: 'rgba(116, 212, 154, 0.14)',
            color: '#74d49a',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            marginBottom: 16,
          }}
        >
          <span
            aria-hidden
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#74d49a',
              boxShadow: '0 0 0 3px rgba(116, 212, 154, 0.22)',
            }}
          />
          Demo · interactive sample
        </div>
        <h2
          id="demo-cta-title"
          style={{
            fontSize: 22,
            fontWeight: 700,
            lineHeight: 1.2,
            margin: '0 0 12px',
            letterSpacing: '-0.01em',
          }}
        >
          Click here to learn more and get a live demonstration of iuvo Guardian.
        </h2>
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.55,
            color: 'rgba(241, 245, 249, 0.78)',
            margin: '0 0 24px',
          }}
        >
          Book a 30-minute walkthrough with the iuvo team. We'll show you Guardian
          running against a real Microsoft 365 tenant — drift, posture, MFA, the
          works — and answer your specific questions.
        </p>
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            width: '100%',
            padding: '12px 18px',
            background: '#74d49a',
            color: '#0b1220',
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: '0.02em',
            borderRadius: 10,
            textDecoration: 'none',
            transition: 'background-color 120ms ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#8ce0ab')}
          onMouseLeave={(e) => (e.currentTarget.style.background = '#74d49a')}
        >
          Schedule a live demo
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path
              d="M9 2h5v5M14 2L6 10M7 14H2V9"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            background: 'transparent',
            border: 0,
            color: 'rgba(241, 245, 249, 0.55)',
            fontSize: 18,
            cursor: 'pointer',
            padding: 6,
            lineHeight: 1,
            borderRadius: 6,
          }}
          aria-label="Close"
        >
          ×
        </button>
      </div>
    </div>
  )
}
